<template>
  <header
    :class="[
      'sticky top-0 z-100 transition-transform duration-300 ease-in-out',
      isMenuView
        ? 'border-b border-border bg-white/95 backdrop-blur'
        : isTransparentHeader
          ? 'bg-transparent border-transparent'
          : 'bg-white shadow-sm',
      !isMenuView && isHeaderHidden ? '-translate-y-full' : 'translate-y-0',
    ]"
  >
    <!-- Menu View Header -->
    <div
      v-if="isMenuView"
      class="container mx-auto flex items-center gap-2.5 px-4 py-2.5 md:px-6 md:py-4 lg:px-8"
    >
      <button
        type="button"
        class="flex h-8 w-8 items-center justify-center rounded-full border border-border text-text-primary transition-colors duration-200 hover:bg-border-light md:h-10 md:w-10"
        aria-label="Go back"
        @click="goBackFromMenu"
      >
        <PhArrowLeft :size="17" weight="bold" />
      </button>
      <div class="min-w-0 flex-1 text-center">
        <p class="text-[14px] font-semibold text-text-primary md:text-[16px]">Menu</p>
      </div>
      <div class="flex items-center gap-2">
        <router-link
          v-if="menuImagesUrl && hasPdfOrImageMenu"
          :to="menuImagesUrl"
          class="inline-flex items-center gap-1.5 rounded-xl border border-border bg-white px-3 py-1.5 text-xs font-semibold text-text-primary transition hover:bg-border-light shadow-sm"
        >
          <PhImageSquare :size="15" weight="bold" class="text-primary shrink-0" />
          <span class="hidden sm:inline">View Menu Images</span>
        </router-link>

        <router-link
          v-if="isSmartDineInEnabled && !hasActiveReservation"
          :to="activateOrderUrl"
          class="inline-flex items-center gap-1.5 rounded-xl bg-green px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-primary/90 shadow-sm"
        >
          <PhLightning :size="15" weight="bold" class="shrink-0" />
          <span class="hidden sm:inline">Activate Order</span>
          <span class="sm:hidden ">Activate</span>
        </router-link>
      </div>
    </div>

    <!-- Default Header -->
    <div
      v-else
      class="relative mx-auto flex h-[56px] items-center justify-between px-3.5 md:h-[70px] md:px-4 lg:px-8 xl:px-20"
    >
      <div class="flex items-center gap-2.5 md:gap-4">
        <button
          v-if="shouldShowHamburger"
          class="block cursor-pointer border-none bg-transparent p-1 text-white transition-colors duration-200 hover:opacity-70 md:hidden"
          :class="isTransparentHeader ? 'text-white' : 'text-text-primary'"
          @click="handleHamburgerClick"
          aria-label="Open menu"
        >
          <PhList :size="20" weight="regular" />
        </button>
        <router-link to="/" class="no-underline">
          <Logo :small="windowWidth < 768" />
        </router-link>
      </div>

      <div class="flex items-center gap-1.5 md:hidden">
        <template v-if="isAuthenticated">
          <button
            class="relative cursor-pointer border-none bg-transparent p-1 text-white transition-opacity duration-200 hover:opacity-70"
            :class="isTransparentHeader ? 'text-white' : 'text-text-primary'"
            title="Notifications"
            @click="handleNotificationsClick"
          >
            <PhBell :size="20" weight="regular" />
            <span
              v-if="notificationsCount > 0"
              class="absolute -top-1 -right-1 bg-primary text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px]"
            >
              {{ notificationsCount > 9 ? '9+' : notificationsCount }}
            </span>
          </button>

          <button
            class="relative flex h-8.5 w-8.5 items-center justify-center rounded-full bg-primary text-white text-[13px] font-semibold transition-all duration-200 hover:opacity-80"
            :title="fullUserName || 'User Profile'"
            @click="handleProfileClick"
          >
            {{ userInitials }}
            <PhGear
              class="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full bg-white p-0.5 text-primary"
              :size="12"
              weight="regular"
            />
          </button>
        </template>
        <template v-else>
          <button
            class="cursor-pointer rounded-md border-none bg-primary px-3 py-1.75 text-[13px] font-medium text-white no-underline transition-all duration-200 hover:opacity-90"
            @click="handleSignInClick"
          >
            Sign In
          </button>
        </template>
      </div>

      <nav class="hidden md:flex items-center gap-4">
        <template v-if="isAuthenticated">
          <router-link
            :to="myOrdersRoute"
            class="relative flex items-center gap-2 no-underline font-medium text-xs lg:text-sm px-3 py-2 rounded-md transition-all duration-200 hover:opacity-70"
            :class="
              isTransparentHeader
                ? 'text-white hover:text-white'
                : 'text-text-primary hover:text-primary hover:bg-border-light'
            "
            title="My Orders"
          >
            <PhPackage
              :size="20"
              weight="regular"
              :class="isTransparentHeader ? 'text-white' : 'text-text-primary'"
            />
            <span
              class="hidden lg:inline"
              :class="isTransparentHeader ? 'text-white' : 'text-text-primary'"
              >My Orders</span
            >
            <span
              v-if="ordersCount > 0"
              class="absolute -top-1 -right-1 bg-warning text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px]"
            >
              {{ ordersCount > 9 ? '9+' : ordersCount }}
            </span>
          </router-link>

          <router-link
            :to="myReservationsRoute"
            class="relative flex items-center gap-2 no-underline font-medium text-xs lg:text-sm px-3 py-2 rounded-md transition-all duration-200 hover:opacity-70"
            :class="
              isTransparentHeader
                ? 'text-white hover:text-white'
                : 'text-text-primary hover:text-primary hover:bg-border-light'
            "
            title="My Reservations"
          >
            <PhCalendarCheck
              :size="20"
              weight="regular"
              :class="isTransparentHeader ? 'text-white' : 'text-text-primary'"
            />
            <span
              class="hidden lg:inline"
              :class="isTransparentHeader ? 'text-white' : 'text-text-primary'"
              >My Reservations</span
            >
            <span
              v-if="reservationsCount > 0"
              class="absolute -top-1 -right-1 bg-primary text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px]"
            >
              {{ reservationsCount > 9 ? '9+' : reservationsCount }}
            </span>
          </router-link>

          <button
            class="relative flex items-center gap-2 no-underline font-medium text-xs lg:text-sm px-3 py-2 rounded-md transition-all duration-200 hover:opacity-70"
            :class="
              isTransparentHeader
                ? 'text-white hover:text-white'
                : 'text-text-primary hover:text-primary hover:bg-border-light'
            "
            title="Notifications"
            @click="handleNotificationsClick"
          >
            <PhBell
              :size="20"
              weight="regular"
              :class="isTransparentHeader ? 'text-white' : 'text-text-primary'"
            />
            <span
              v-if="notificationsCount > 0"
              class="absolute -top-1 -right-1 bg-primary text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px]"
            >
              {{ notificationsCount > 9 ? '9+' : notificationsCount }}
            </span>
          </button>

          <!-- Desktop cart icon + dropdown -->
          <div class="relative">
            <button
              class="relative flex items-center gap-2 no-underline font-medium text-xs lg:text-sm px-3 py-2 rounded-md transition-all duration-200 hover:opacity-70"
              :class="
                isTransparentHeader
                  ? 'text-white hover:text-white'
                  : 'text-text-primary hover:text-primary hover:bg-border-light'
              "
              title="Cart"
              @click="handleCartClick"
            >
              <span :key="cartBumpTick" class="inline-flex animate-cart-bump">
                <PhShoppingCart
                  :size="20"
                  weight="regular"
                  :class="isTransparentHeader ? 'text-white' : 'text-text-primary'"
                />
              </span>
              <span
                v-if="cartItemCount > 0"
                :key="`cart-badge-${cartBumpTick}`"
                class="absolute -top-1 -right-1 bg-danger text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px] animate-cart-bump"
              >
                {{ cartItemCount > 9 ? '9+' : cartItemCount }}
              </span>
            </button>
            <CartDropdownPanel
              v-if="showCartDropdown"
              :items="orderStore.cart.items"
              :total="orderStore.cartTotal"
              @close="showCartDropdown = false"
              @open-cart="handleOpenCartFromDropdown"
            />
          </div>

          <button
            class="relative flex items-center justify-center w-10 h-10 rounded-full bg-primary text-white font-semibold text-sm transition-all duration-200 hover:bg-bg-hover"
            :title="fullUserName || 'User Profile'"
            @click="handleProfileClick"
          >
            {{ userInitials }}
            <PhGear
              class="absolute bottom-0 right-0 w-4 h-4 bg-white rounded-full p-0.5 text-primary"
              :size="14"
              weight="regular"
            />
          </button>
        </template>

        <template v-else>
          <router-link
            to="/restaurant-signup"
            class="flex items-center gap-2 no-underline font-medium text-xs lg:text-sm px-4 py-2 rounded-md transition-all duration-200 hover:opacity-70"
            :class="
              isTransparentHeader
                ? 'text-white hover:text-white'
                : 'text-text-primary hover:text-primary hover:bg-border-light'
            "
          >
            <PhHandshake
              :size="18"
              weight="regular"
              :class="isTransparentHeader ? 'text-white' : 'text-text-primary'"
            />
            <span :class="isTransparentHeader ? 'text-white' : 'text-text-primary'"
              >Partner with us</span
            >
          </router-link>
          <button
            class="flex items-center gap-2 no-underline font-medium text-xs lg:text-sm px-4 py-2 rounded-md transition-all duration-200 hover:opacity-70 bg-transparent border-none cursor-pointer"
            :class="
              isTransparentHeader
                ? 'text-white hover:text-white'
                : 'text-text-primary hover:text-primary hover:bg-border-light'
            "
            @click="handleSignInClick"
          >
            <span :class="isTransparentHeader ? 'text-white' : 'text-text-primary'">Sign In</span>
          </button>
          <button
            class="bg-primary text-white px-6 py-2 rounded-md no-underline font-medium text-xs lg:text-sm transition-all duration-200 hover:bg-bg-hover border-none cursor-pointer"
            @click="handleSignUpClick"
          >
            Sign Up
          </button>
        </template>
      </nav>
    </div>
  </header>
  <!-- Backdrop to close cart dropdown on outside click -->
  <div v-if="showCartDropdown" class="fixed inset-0 z-40" @click="showCartDropdown = false" />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useOrderStore } from '@/stores/order'
import { useRestaurantStore } from '@/stores/restaurant'
import { useActiveReservation } from '@/composables/features/useActiveReservation'
import { useAppFlow } from '@/composables/core/useAppFlow'
import { useHeaderScroll } from '@/composables/core/useHeaderScroll'
import { useWindowWidth } from '@/composables/core/useWindowWidth'
import Logo from '@/components/atoms/Logo.vue'
import CartDropdownPanel from '@/components/features/order/CartDropdownPanel.vue'
import {
  PhList,
  PhHandshake,
  PhPackage,
  PhCalendarCheck,
  PhBell,
  PhShoppingCart,
  PhGear,
  PhArrowLeft,
  PhImageSquare,
  PhLightning,
} from '@phosphor-icons/vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const orderStore = useOrderStore()
const restaurantStore = useRestaurantStore()
const { getStoredActiveReservation } = useActiveReservation()
const { cartBumpTick } = storeToRefs(orderStore)
const { currentFlow, getContextualRoute } = useAppFlow()

const showCartDropdown = ref<boolean>(false)

const isMenuView = computed((): boolean => route.name === 'RestaurantMenu')

const hasActiveReservation = computed((): boolean => {
  if (route.query.reservation_id) return true
  if (authStore.activeReservationId) return true
  const stored = getStoredActiveReservation()
  if (stored?.reservationId) return true
  return false
})

const menuTenantId = computed((): string => {
  return (
    (route.params.tenantId as string | undefined) ||
    restaurantStore.currentTenantId ||
    (restaurantStore.currentRestaurant?.tenant_id != null
      ? String(restaurantStore.currentRestaurant.tenant_id)
      : '')
  )
})

function hasMediaCollectionItems(collection: unknown): boolean {
  if (!Array.isArray(collection) || collection.length === 0) return false
  return collection.some(item => {
    if (!item || typeof item !== 'object') return false
    const mediaItem = item as Record<string, unknown>
    const url =
      mediaItem.original_url || mediaItem.url || mediaItem.preview_url || mediaItem.file_name
    return typeof url === 'string' && url.trim() !== ''
  })
}

const hasPdfOrImageMenu = computed((): boolean => {
  const rest = restaurantStore.currentRestaurant as Record<string, unknown> | null
  if (!rest) return true

  const hasPdf = hasMediaCollectionItems(rest.menu_pdf_collection)
  const hasMenuImages = hasMediaCollectionItems(rest.menu_images_collection)
  const hasBeverageImages = hasMediaCollectionItems(rest.beverage_menu_images_collection)
  const hasDessertImages = hasMediaCollectionItems(rest.dessert_menu_images_collection)
  const hasActivityImages = hasMediaCollectionItems(rest.activity_menu_images_collection)

  return hasPdf || hasMenuImages || hasBeverageImages || hasDessertImages || hasActivityImages
})

const menuImagesUrl = computed((): string => {
  if (!menuTenantId.value) return ''
  return `/menu-view/${menuTenantId.value}`
})

const activateOrderUrl = computed((): string => {
  if (!menuTenantId.value) return '#'
  return `/restaurant-menu-view/${menuTenantId.value}?activate_order=true`
})

const isSmartDineInEnabled = computed((): boolean => {
  const status = restaurantStore.currentRestaurant?.smart_dine_in_status
  return status !== 0 && status !== false
})

const goBackFromMenu = (): void => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  if (currentFlow.value === 'reserve-table') {
    router.push({
      path: '/reservations',
      query: { flow: 'reserve-table' },
    })
    return
  }

  if (currentFlow.value === 'smart-dine-in') {
    router.push({
      path: '/smart-dine-in',
      query: { flow: 'smart-dine-in' },
    })
    return
  }

  router.push('/')
}

const isAuthenticated = computed(() => authStore.isAuthenticated)
const fullUserName = computed(() => authStore.fullUserName)
const cartItemCount = computed(() => orderStore.cartItemCount)
const myOrdersRoute = computed(() => getContextualRoute('/my-orders'))
const myReservationsRoute = computed(() => getContextualRoute('/my-reservations'))
const shouldShowHamburger = computed(
  () => isAuthenticated.value || route.meta.showMobileMenu === true
)

const isTransparentHeader = computed(() => {
  return route.name === 'Home' || route.name === 'Login' || route.name === 'Register'
})

const userInitials = computed(() => {
  if (!authStore.user) return 'U'
  const firstName = authStore.user.first_name || ''
  const lastName = authStore.user.last_name || ''
  const firstInitial = firstName.charAt(0).toUpperCase()
  const lastInitial = lastName.charAt(0).toUpperCase()
  return lastInitial ? `${firstInitial}${lastInitial}` : firstInitial || 'U'
})

const ordersCount = ref(0)
const reservationsCount = ref(0)
const notificationsCount = ref(0)

const { windowWidth } = useWindowWidth()
// Scroll detection for header visibility
const { isHeaderHidden } = useHeaderScroll()

const handleHamburgerClick = (): void => {
  router.push({
    path: route.path,
    query: { ...route.query, drawer: 'settings' },
  })
}

const handleProfileClick = (): void => {
  router.push({
    path: route.path,
    query: { ...route.query, drawer: 'settings' },
  })
}

const handleCartClick = (): void => {
  showCartDropdown.value = !showCartDropdown.value
}

/** Called when user clicks "View Cart & Place Order" inside the dropdown. Uses the same
 *  URL-push strategy as FlowBottomNav so the existing validation watchers in MenuView /
 *  FlowLayout fire reliably regardless of which component is currently mounted. */
const handleOpenCartFromDropdown = (): void => {
  showCartDropdown.value = false
  if (currentFlow.value === 'smart-dine-in') {
    // Push drawer=order — MenuView's route.query.drawer watcher picks this up and runs validation
    router.push({ path: route.path, query: { ...route.query, drawer: 'order' } })
  } else {
    // Non-smart-dine-in: trigger modal via store (MenuView must be mounted as RestaurantMenu)
    orderStore.openCartModal()
  }
}

const handleNotificationsClick = (): void => {}

const handleSignInClick = (): void => {
  router.push({
    path: route.path,
    query: { ...route.query, drawer: 'login' },
  })
}

const handleSignUpClick = (): void => {
  router.push({
    path: route.path,
    query: { ...route.query, drawer: 'register' },
  })
}
</script>
